import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Select } from 'antd'

import { fetchProjectEpicOptions } from '../../taskRefactored/components/epicSelector/epicOptionsCache'

import styles from './TaskParentEpicModal.module.css'

const TaskParentEpicModal = ({
    open,
    mode = 'add',
    projectId,
    initialEpicId = null,
    taskTitle = '',
    onCancel,
    onConfirm,
}) => {
    const [epicOptions, setEpicOptions] = useState([])
    const [loadingEpics, setLoadingEpics] = useState(false)
    const [selectedEpicId, setSelectedEpicId] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const selectWrapRef = useRef(null)

    const isChangeMode = mode === 'change'
    const epicPlaceholder = 'Choose an epic'

    const title = isChangeMode ? 'Change parent' : 'Add parent'
    const subtitle = isChangeMode
        ? 'Move this task to a different epic parent.'
        : 'Link this task to an epic parent.'
    const confirmLabel = isChangeMode ? 'Save changes' : 'Add parent'

    useEffect(() => {
        if (!open) {
            setSelectedEpicId(null)
            setSubmitting(false)
            return undefined
        }

        setSelectedEpicId(
            initialEpicId != null && initialEpicId !== ''
                ? String(initialEpicId)
                : null
        )

        if (!projectId) {
            setEpicOptions([])
            return undefined
        }

        let cancelled = false
        setLoadingEpics(true)

        fetchProjectEpicOptions(projectId)
            .then((list) => {
                if (!cancelled) {
                    setEpicOptions(list || [])
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setEpicOptions([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingEpics(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [open, projectId, initialEpicId])

    useEffect(() => {
        if (!open) return undefined
        const syncSearchInput = () => {
            const input = selectWrapRef.current?.querySelector(
                '.ant-select-selection-search-input'
            )
            if (input) {
                input.setAttribute('placeholder', epicPlaceholder)
            }
        }
        syncSearchInput()
        const id = window.requestAnimationFrame(syncSearchInput)
        return () => window.cancelAnimationFrame(id)
    }, [open, selectedEpicId, loadingEpics, epicPlaceholder])

    const selectOptions = useMemo(
        () =>
            epicOptions.map((item) => ({
                value: String(item.value),
                label: item.label,
                epicKey: item.epicKey,
            })),
        [epicOptions]
    )

    const selectedEpic = useMemo(
        () =>
            epicOptions.find(
                (item) => String(item.value) === String(selectedEpicId)
            ) || null,
        [epicOptions, selectedEpicId]
    )

    const renderEpicLabel = (epicKey, label) => (
        <span className={styles.epicValue}>
            {epicKey ? (
                <span className={styles.epicValueKey}>{epicKey}</span>
            ) : null}
            <span className={styles.epicValueTitle}>{label}</span>
        </span>
    )

    const handleConfirm = useCallback(async () => {
        if (!selectedEpicId) return
        setSubmitting(true)
        try {
            const ok = await onConfirm?.(selectedEpicId)
            if (ok !== false) {
                onCancel?.()
            }
        } finally {
            setSubmitting(false)
        }
    }, [selectedEpicId, onConfirm, onCancel])

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            closable
            destroyOnHidden
            className={styles.taskParentEpicModal}
            width={440}
            centered
            maskClosable={!submitting}
            getContainer={() => document.body}
            zIndex={1100}
        >
            <div className={styles.taskParentEpicModalContent}>
                <div className={styles.taskParentEpicHeader}>
                    <h3 className={styles.taskParentEpicModalTitle}>{title}</h3>
                    <p className={styles.taskParentEpicModalSubtitle}>
                        {subtitle}
                    </p>
                </div>

                {taskTitle ? (
                    <div className={styles.taskParentEpicTaskRow}>
                        <span className={styles.taskParentEpicTaskLabel}>
                            Task
                        </span>
                        <span
                            className={styles.taskParentEpicTaskName}
                            title={taskTitle}
                        >
                            {taskTitle}
                        </span>
                    </div>
                ) : null}

                <div className={styles.taskParentEpicField}>
                    <label
                        className={styles.taskParentEpicFieldLabel}
                        htmlFor="task-parent-epic-select"
                    >
                        Epic
                    </label>
                    <div
                        ref={selectWrapRef}
                        className={styles.taskParentEpicSelectWrap}
                    >
                        <Select
                            id="task-parent-epic-select"
                            className={styles.taskParentEpicSelect}
                            popupClassName={styles.taskParentEpicSelectDropdown}
                            placeholder={epicPlaceholder}
                            value={selectedEpicId}
                            onChange={setSelectedEpicId}
                            options={selectOptions}
                            loading={loadingEpics}
                            showSearch
                            optionFilterProp="label"
                            disabled={submitting}
                            allowClear={!isChangeMode}
                            suffixIcon={null}
                            optionRender={(option) =>
                                renderEpicLabel(
                                    option.data?.epicKey,
                                    option.data?.label ?? option.label
                                )
                            }
                            labelRender={() => {
                                if (selectedEpic) {
                                    return renderEpicLabel(
                                        selectedEpic.epicKey,
                                        selectedEpic.label
                                    )
                                }
                                if (selectedEpicId && loadingEpics) {
                                    return (
                                        <span className={styles.epicValueTitle}>
                                            Loading…
                                        </span>
                                    )
                                }
                                return undefined
                            }}
                        />
                    </div>
                </div>

                <div className={styles.taskParentEpicModalFooter}>
                    <Button
                        onClick={onCancel}
                        disabled={submitting}
                        className={styles.taskParentEpicCancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleConfirm}
                        loading={submitting}
                        disabled={!selectedEpicId}
                        className={styles.taskParentEpicConfirmButton}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default TaskParentEpicModal
